/**
 * Utilidades para optimización y almacenamiento eficiente de firmas digitales en K-LyDapp.
 * Reduce el peso del Base64 en más del 85-90% mediante recorte de márgenes vacíos (auto-crop)
 * y soporta la subida opcional a Supabase Storage cuando hay conectividad a internet.
 */

/**
 * Optimiza un canvas de firma digital:
 * 1. Detecta los límites reales del trazo (elimina todo el lienzo blanco/transparente sobrante).
 * 2. Si el canvas está vacío, devuelve cadena vacía.
 * 3. Redimensiona proporcionalmente a una resolución ligera y nítida (máximo 260x110 píxeles).
 * 4. Genera una imagen compacta que reduce drásticamente el uso de memoria y cuota local.
 */
export function optimizeSignatureCanvas(canvas: HTMLCanvasElement): string {
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas.toDataURL('image/png');

    const width = canvas.width;
    const height = canvas.height;
    
    let imgData: ImageData;
    try {
        imgData = ctx.getImageData(0, 0, width, height);
    } catch (e) {
        // En caso de canvas contaminado por CORS o no legible
        return canvas.toDataURL('image/png');
    }

    const data = imgData.data;

    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;

    // Detectar píxeles que contengan trazo (no transparentes y no blancos puros)
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const idx = (y * width + x) * 4;
            const r = data[idx];
            const g = data[idx + 1];
            const b = data[idx + 2];
            const a = data[idx + 3];

            // Consideramos parte de la firma si tiene opacidad visible y no es fondo blanco
            const isFilled = a > 25 && !(r > 240 && g > 240 && b > 240);
            if (isFilled) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
            }
        }
    }

    // Si no se detectó ningún trazo
    if (maxX === -1 || maxY === -1) {
        return '';
    }

    // Margen de seguridad estético (padding de 6px)
    const pad = 6;
    const cropX = Math.max(0, minX - pad);
    const cropY = Math.max(0, minY - pad);
    const cropW = Math.min(width - cropX, (maxX - minX) + pad * 2);
    const cropH = Math.min(height - cropY, (maxY - minY) + pad * 2);

    // Escalar proporcionalmente para que no supere 260px de ancho ni 110px de alto
    const maxTargetW = 260;
    const maxTargetH = 110;
    const scale = Math.min(1, maxTargetW / cropW, maxTargetH / cropH);

    const targetW = Math.max(20, Math.round(cropW * scale));
    const targetH = Math.max(20, Math.round(cropH * scale));

    const outCanvas = document.createElement('canvas');
    outCanvas.width = targetW;
    outCanvas.height = targetH;
    const outCtx = outCanvas.getContext('2d');
    if (!outCtx) return canvas.toDataURL('image/png');

    outCtx.imageSmoothingEnabled = true;
    outCtx.imageSmoothingQuality = 'high';
    outCtx.drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, targetW, targetH);

    return outCanvas.toDataURL('image/png');
}

/**
 * Sube una firma digital a Supabase Storage si el dispositivo está en línea.
 * Si falla o está fuera de línea, devuelve el Base64 original sin interrumpir el flujo del usuario.
 */
export async function uploadSignatureIfOnline(
    supabaseClient: any,
    base64Data: string,
    folder: string,
    filenamePrefix: string
): Promise<string> {
    if (!base64Data || !base64Data.startsWith('data:image')) {
        return base64Data;
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return base64Data;
    }

    try {
        const res = await fetch(base64Data);
        const blob = await res.blob();
        const safePrefix = filenamePrefix.replace(/[^a-zA-Z0-9_-]/g, '_');
        const filePath = `${folder}/${safePrefix}_${Date.now()}.png`;

        const { data, error } = await supabaseClient.storage
            .from('graphic_plans')
            .upload(filePath, blob, {
                contentType: 'image/png',
                upsert: true,
                cacheControl: '86400'
            });

        if (error || !data) {
            console.warn('[uploadSignatureIfOnline] Advertencia en subida de firma, conservando base64:', error);
            return base64Data;
        }

        return data.path;
    } catch (err) {
        console.warn('[uploadSignatureIfOnline] Excepción al subir firma a storage, usando base64 local:', err);
        return base64Data;
    }
}
